// Pure merge engine for the profile-page collection import (CSV + XLSX).
// Zero imports on purpose, mirroring collectionExport.mjs: everything
// app-specific (supabase, SET_REGISTRY, set files, SheetJS parsing) is
// injected by the runner in collectionImportRun.js, so
// `node --test src/lib/collectionImport.test.mjs` loads this file directly.
//
// The import contract IS the export format (EXPORT_COLUMNS in
// collectionExport.mjs). Rows are matched by the Card Key column; rows
// without one are resolved against injected set data to construct the key.
//
// card_key grammar (see src/lib/cardKey.js, the canonical parser):
//   <setId>__<subSetId>__<cardN>[__parallel__<parallelId>][__s__<i>]
// __s__<i> after a parallel segment is a real serial number; with no parallel
// segment it is a multi-entry copy index (v2.4.25 — base cards too).

// ── Column contract ──────────────────────────────────────────────────

// Header -> collections column for every field an import may write.
export const WRITABLE_HEADERS = {
  'Status': 'status',
  'Condition': 'card_condition',
  'Grading Company': 'grading_company',
  'Grade': 'grading_grade',
  'Cert #': 'grading_cert_num',
  'Graded Serial': 'grading_serial_num',
  'Graded Print Run': 'grading_print_run',
  'TAG Score': 'grading_tag_score',
  'Pop This Grade': 'grading_pop_this',
  'Pop Higher': 'grading_pop_higher',
  'Report URL': 'grading_report_url',
  'Sale Price': 'sale_price',
  'Willing To Pay': 'willing_to_pay',
  'Trade Value': 'trade_value',
  'Trade Notes': 'trade_notes',
  'Sketch Subject': 'sketch_subject',
  'Sketch Parallel': 'sketch_parallel',
  // PSEUDO-COLUMN: private_note is NOT a column on collections. It lives in
  // public.collection_notes (owner-only RLS), so the runner strips it out of
  // the payload before the collections upsert and writes it separately.
  'Private Notes': 'private_note',
};

// Fields that ride through the planner but must never reach the collections
// upsert — PostgREST would reject the request with an unknown-column error.
export const PSEUDO_COLUMNS = new Set(['private_note']);

// Identity headers key the row to a card; display-only headers are ignored.
// Set Name is identity too: users migrating from other trackers know names,
// not our ids — either column resolves the set.
export const IDENTITY_HEADERS = {
  'Card Key': 'cardKey',
  'Set ID': 'setId',
  'Set Name': 'setName',
  'Subset': 'subset',
  'Card #': 'cardN',
  'Parallel': 'parallel',
  'Serial #': 'serial',
};

// collections columns that are numeric in Postgres (everything else the
// import writes is text). Verified against staging 2026-08-03.
export const NUMERIC_COLUMNS = new Set(['sale_price', 'willing_to_pay', 'trade_value']);

// Mirrors the status CHECK constraint so bad rows fail in preview.
export const VALID_STATUSES = ['collected', 'wishlist', 'trade', 'for_sale'];

const normHeader = (h) => String(h ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

const WRITABLE_BY_NORM = new Map(Object.entries(WRITABLE_HEADERS).map(([h, col]) => [normHeader(h), { header: h, col }]));
const IDENTITY_BY_NORM = new Map(Object.entries(IDENTITY_HEADERS).map(([h, slot]) => [normHeader(h), { header: h, slot }]));

// Export headers that are known but never written (display-only).
const KNOWN_IGNORED = new Set([
  'type', 'card name', 'artist/subject', 'print run',
  'photo front url', 'photo back url', 'date added', 'date sold',
].map(normHeader));

// ── Key parsing (frozen grammar, duplicated from cardKey.js on purpose) ──

export function parseKey(cardKey) {
  let key = String(cardKey || '');
  let serialNum = null;
  if (key.includes('__s__')) {
    const i = key.lastIndexOf('__s__');
    serialNum = key.slice(i + 5);
    key = key.slice(0, i);
  }
  const parts = key.split('__');
  let setId = '', subSetId = '', cardN = '', parallelId = null;
  const pIdx = parts.indexOf('parallel');
  if (pIdx !== -1) parallelId = parts.slice(pIdx + 1).join('__');
  const kParts = pIdx !== -1 ? parts.slice(0, pIdx) : parts;
  if (kParts.length >= 3) {
    cardN = kParts[kParts.length - 1];
    subSetId = kParts[kParts.length - 2];
    setId = kParts.slice(0, kParts.length - 2).join('__');
  }
  return { setId, subSetId, cardN, parallelId, serialNum };
}

const baseOf = (key) => {
  const i = key.lastIndexOf('__s__');
  return i === -1 ? key : key.slice(0, i);
};

// A key is structurally valid when it parses to set/subset/card and any
// __s__ tail is a positive integer (both serials and copy indices are).
function keyLooksValid(key) {
  const { setId, subSetId, cardN, serialNum } = parseKey(key);
  if (!setId || !subSetId || !cardN) return false;
  if (serialNum !== null && !/^\d+$/.test(serialNum)) return false;
  return true;
}

// ── Header mapping ───────────────────────────────────────────────────

// Map one header row. Order never matters; unknown headers are reported.
export function mapHeaders(headerRow) {
  const cols = [];        // per index: { kind:'writable'|'identity'|'ignored'|'unknown', col?, slot?, header }
  const present = new Set();  // writable collections columns present in this sheet
  const identity = {};        // slot -> column index
  const unknown = [];
  (headerRow || []).forEach((raw, i) => {
    const n = normHeader(raw);
    if (!n) { cols.push({ kind: 'ignored', header: '' }); return; }
    const w = WRITABLE_BY_NORM.get(n);
    if (w) { cols.push({ kind: 'writable', col: w.col, header: w.header }); present.add(w.col); return; }
    const id = IDENTITY_BY_NORM.get(n);
    if (id) { cols.push({ kind: 'identity', slot: id.slot, header: id.header }); identity[id.slot] = i; return; }
    if (KNOWN_IGNORED.has(n)) { cols.push({ kind: 'ignored', header: String(raw) }); return; }
    cols.push({ kind: 'unknown', header: String(raw) });
    unknown.push(String(raw));
  });
  const isData = identity.cardKey !== undefined
    || ((identity.setId !== undefined || identity.setName !== undefined) && identity.cardN !== undefined);
  return { cols, present, identity, unknown, isData };
}

// ── Cell coercion ────────────────────────────────────────────────────

const cellStr = (v) => {
  if (v === null || v === undefined) return '';
  return String(v).trim();
};

// Coerce a present cell to its DB value. Empty cell -> null (clears field).
// Returns { value } or { error }.
export function coerceValue(col, raw) {
  const s = cellStr(raw);
  if (s === '') return { value: null };
  if (col === 'status') {
    const v = s.toLowerCase().replace(/\s+/g, '_');
    if (!VALID_STATUSES.includes(v)) {
      return { error: `Status "${s}" isn't valid. Use ${VALID_STATUSES.join(', ')}.` };
    }
    return { value: v };
  }
  if (NUMERIC_COLUMNS.has(col)) {
    const n = Number(s.replace(/[$,\s]/g, ''));
    if (!Number.isFinite(n)) return { error: `"${s}" isn't a number.` };
    return { value: n };
  }
  return { value: s };
}

// Loose equality between a DB value and a coerced import value: null and ''
// are the same emptiness; numerics compare as numbers; text compares trimmed.
export function sameValue(col, dbVal, impVal) {
  const dbEmpty = dbVal === null || dbVal === undefined || String(dbVal).trim() === '';
  const impEmpty = impVal === null || impVal === undefined || String(impVal).trim() === '';
  if (dbEmpty || impEmpty) return dbEmpty === impEmpty;
  if (NUMERIC_COLUMNS.has(col)) return Number(dbVal) === Number(impVal);
  return String(dbVal).trim() === String(impVal).trim();
}

// ── Loose matching (user-first: the app resolves, users don't format) ─

const eq = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

const normLoose = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const tokensOf = (s) => normLoose(s).split(' ').filter(Boolean);

// Match a user-typed label against candidates: exact normalized equality on
// any alias first, then full-token containment ("2026 galaxy" hits
// "2026 Topps Chrome Star Wars Galaxy"). A unique hit is required.
// Returns { hit } | { ambiguous: [...] } | null.
export function looseMatch(label, candidates, aliasesOf) {
  const n = normLoose(label);
  if (!n) return null;
  const exact = candidates.filter((c) => aliasesOf(c).some((a) => normLoose(a) === n));
  if (exact.length === 1) return { hit: exact[0] };
  if (exact.length > 1) return { ambiguous: exact };
  const lt = tokensOf(label);
  const contains = candidates.filter((c) => aliasesOf(c).some((a) => {
    const at = new Set(tokensOf(a));
    return lt.every((t) => at.has(t));
  }));
  if (contains.length === 1) return { hit: contains[0] };
  if (contains.length > 1) return { ambiguous: contains };
  return null;
}

// Best-effort "did you mean" for error messages: token overlap plus a
// substring bonus, threshold-gated so bad suggestions stay silent.
export function suggestMatch(label, candidates, aliasesOf) {
  const lt = tokensOf(label);
  if (!lt.length) return null;
  let best = null, bestScore = 0;
  for (const c of candidates) {
    for (const a of aliasesOf(c)) {
      const at = tokensOf(a);
      if (!at.length) continue;
      const aset = new Set(at);
      let overlap = 0;
      for (const t of lt) if (aset.has(t)) overlap++;
      let score = overlap / Math.max(lt.length, at.length);
      const nl = normLoose(label), na = normLoose(a);
      if (na.includes(nl) || nl.includes(na)) score += 0.3;
      if (!overlap && lt.some((t) => at.some((x) => x.includes(t) || t.includes(x)))) score += 0.2;
      if (score > bestScore) { bestScore = score; best = c; }
    }
  }
  return bestScore >= 0.4 ? best : null;
}

// The parallels that apply to a subset, mirroring ShareModal's resolution:
// the subset's own array when present, else the set's type ladder
// (sd.ladders = { base, insert, auto }, already merged with per-set
// overrides by the runner). Sketch subsets fall through to base, same as
// the UI — sketch keys never carry __parallel__ in practice.
export function parallelsForSubset(sd, sub) {
  if (sub.parallels && sub.parallels.length) return sub.parallels;
  const ladders = (sd && sd.ladders) || {};
  const type = String(sub.type || '');
  if (type.startsWith('auto')) return ladders.auto || [];
  if (type === 'insert') return ladders.insert || [];
  return ladders.base || [];
}

// Parse a Serial # cell: "copy 3" -> { copy: 3 }, "12" -> { num: 12 }.
function parseSerialCell(s) {
  const m = /^copy\s+(\d+)$/i.exec(s);
  if (m) return { copy: Number(m[1]) };
  if (/^\d+$/.test(s)) return { num: Number(s) };
  return null;
}

// Construct a card_key from identity cells against injected set data.
// setData: { [setId]: { name, sets: SETS, ladders } } — the runner's shape.
// registry: [{ id, name }] for the whole catalog, so set resolution and
// did-you-mean work even for sets whose data isn't loaded.
// Matching is deliberately forgiving: users type what they know ("2026
// Galaxy", "Base Set", "Wave") and the app resolves it; only genuine
// ambiguity or absence errors — with a suggestion when one is close.
// Returns { key } or { error, errType: 'identity' }.
export function resolveKey(cells, setData, registry) {
  const idErr = (error) => ({ error, errType: 'identity' });
  const sidCell = cellStr(cells.setId);
  const snameCell = cellStr(cells.setName);
  const cardN = cellStr(cells.cardN);
  const subsetLabel = cellStr(cells.subset);
  const parallelLabel = cellStr(cells.parallel);
  const serialLabel = cellStr(cells.serial);

  if (!sidCell && !snameCell) return idErr('No Card Key, and no Set ID or Set Name to find the set with.');
  if (!cardN) return idErr('No Card Key and no Card # — one of the two is needed.');

  // Set: exact id, then loose id/name against the full catalog.
  let setId = setData[sidCell] ? sidCell : null;
  if (!setId) {
    const cands = (registry && registry.length)
      ? registry
      : Object.keys(setData).map((id) => ({ id, name: setData[id].name || id }));
    const aliases = (c) => [c.id, c.name];
    const label = sidCell || snameCell;
    const m = looseMatch(label, cands, aliases);
    if (m && m.hit) setId = m.hit.id;
    else if (m && m.ambiguous) {
      // candidates: structured, so the preview can offer one-click picks.
      return {
        ...idErr(`"${label}" matches ${m.ambiguous.length} sets — pick the right one below.`),
        candidates: m.ambiguous.slice(0, 6).map((c) => ({ id: c.id, name: c.name || c.id })),
      };
    } else {
      const sug = suggestMatch(label, cands, aliases);
      return {
        ...idErr(`Set "${label}" isn't in the CK catalog.${sug ? ` Did you mean "${sug.name || sug.id}"?` : ''}`),
        candidates: sug ? [{ id: sug.id, name: sug.name || sug.id }] : [],
      };
    }
  }
  const sd = setData[setId];
  if (!sd) return idErr(`Set "${sidCell || snameCell}" matched ${setId}, but its checklist isn't loaded.`);

  const sets = sd.sets || [];
  const subAliases = (s) => [s.id, s.title];
  let subset = null;
  if (subsetLabel) {
    const m = looseMatch(subsetLabel, sets, subAliases);
    if (m && m.hit) subset = m.hit;
    else if (m && m.ambiguous) {
      return idErr(`Subset "${subsetLabel}" matches ${m.ambiguous.length} subsets of ${sd.name || setId} (${m.ambiguous.slice(0, 3).map((s) => s.title || s.id).join(' · ')}) — be more specific.`);
    } else {
      const sug = suggestMatch(subsetLabel, sets, subAliases);
      return idErr(`Subset "${subsetLabel}" isn't in ${sd.name || setId}.${sug ? ` Did you mean "${sug.title || sug.id}"?` : ''}`);
    }
  } else {
    // No subset given: unique card-number match across subsets, else ambiguous.
    const hits = sets.filter((s) => (s.cards || []).some((c) => eq(c.n, cardN)));
    if (hits.length === 1) subset = hits[0];
    else if (hits.length === 0) return idErr(`Card #${cardN} isn't in ${sd.name || setId}.`);
    else return idErr(`Card #${cardN} is in ${hits.length} subsets of ${sd.name || setId} — add a Subset column.`);
  }

  const card = (subset.cards || []).find((c) => eq(c.n, cardN));
  if (!card) return idErr(`Card #${cardN} isn't in subset "${subset.title || subset.id}".`);

  let parallelId = null;
  if (parallelLabel) {
    const pool = parallelsForSubset(sd, subset);
    const pAliases = (p) => [p.id, p.name];
    const m = looseMatch(parallelLabel, pool, pAliases);
    if (m && m.hit) parallelId = m.hit.id;
    else if (m && m.ambiguous) {
      return idErr(`Parallel "${parallelLabel}" matches ${m.ambiguous.length} parallels (${m.ambiguous.slice(0, 3).map((p) => p.name).join(' · ')}) — use the full name.`);
    } else {
      const sug = suggestMatch(parallelLabel, pool, pAliases);
      return idErr(`Parallel "${parallelLabel}" isn't in subset "${subset.title || subset.id}".${sug ? ` Did you mean "${sug.name}"?` : ''}`);
    }
  }

  let key = `${setId}__${subset.id}__${card.n}`;
  if (parallelId) key += `__parallel__${parallelId}`;
  if (serialLabel) {
    const ser = parseSerialCell(serialLabel);
    if (!ser) return idErr(`Serial # "${serialLabel}" isn't a number or "copy N".`);
    if (parallelId) {
      if (ser.copy !== undefined) return idErr(`"${serialLabel}" is a copy index, but this row has a parallel — use the plain serial number.`);
      key += `__s__${ser.num}`;
    } else {
      // No parallel: any number here is a multi-entry copy index.
      key += `__s__${ser.copy !== undefined ? ser.copy : ser.num}`;
    }
  }
  return { key, constructed: true };
}

// ── Classification ───────────────────────────────────────────────────

// classifyImport(sheets, existingRows, ctx)
//   sheets       [{ name, aoa }] — every uploaded sheet, header row first.
//   existingRows raw `collections` rows for the signed-in user.
//   ctx.setData  { [setId]: { sets: SETS } }
//
// Returns:
//   rows      classified data rows (in file order, deduped): { sheet, rowNum,
//             kind: 'new'|'update'|'unchanged'|'error', key, fields, changes,
//             reason?, warnings: [] }
//   counts    { new, update, unchanged, error, warning }
//   errors    [{ sheet, rowNum, reason }]
//   warnings  [{ sheet, rowNum, message }]
//   notes     sheet-level notes (skipped tabs, unknown headers)
//   inserts   payload rows for brand-new cards (status defaulted)
//   updates   payload rows for existing cards (present columns only)
export function classifyImport(sheets, existingRows, ctx) {
  const setData = (ctx && ctx.setData) || {};
  const notes = [];

  // 1. Flatten every data sheet into candidate rows.
  const candidates = [];
  for (const sheet of sheets || []) {
    const aoa = sheet.aoa || [];
    if (!aoa.length) { notes.push(`Skipped empty tab "${sheet.name}".`); continue; }
    const map = mapHeaders(aoa[0]);
    if (!map.isData) { notes.push(`Skipped tab "${sheet.name}" — no Card Key or Set ID + Card # columns.`); continue; }
    if (map.unknown.length) notes.push(`Tab "${sheet.name}": ignored unknown column${map.unknown.length > 1 ? 's' : ''} ${map.unknown.map((h) => `"${h}"`).join(', ')}.`);

    for (let r = 1; r < aoa.length; r++) {
      const rowArr = aoa[r] || [];
      if (rowArr.every((v) => cellStr(v) === '')) continue; // fully blank line
      const values = {};   // writable col -> raw cell
      const idCells = {};  // identity slot -> raw cell
      map.cols.forEach((c, i) => {
        if (c.kind === 'writable') values[c.col] = rowArr[i];
        else if (c.kind === 'identity') idCells[c.slot] = rowArr[i];
      });
      candidates.push({
        sheet: sheet.name,
        rowNum: r + 1, // 1-based, header is row 1 — matches what users see
        present: map.present,
        values,
        idCells,
      });
    }
  }

  const errors = [];
  const warnings = [];
  const warn = (c, message) => { warnings.push({ sheet: c.sheet, rowNum: c.rowNum, message }); };
  // errType 'identity' = the row couldn't be keyed (fixable with the
  // in-preview resolver); 'value' = a present cell wouldn't coerce.
  // cells = the raw identity cells, so the resolver can prefill.
  const fail = (c, reason, errType) => {
    const entry = {
      sheet: c.sheet, rowNum: c.rowNum, reason,
      errType: errType || 'identity',
      cells: c.idCells,
      candidates: c.keyCandidates || [],
    };
    errors.push(entry);
    return { ...entry, kind: 'error', key: c.key || null, fields: {}, changes: [], warnings: [] };
  };

  // 2. Resolve every candidate to a card_key.
  for (const c of candidates) {
    const explicit = cellStr(c.idCells.cardKey);
    if (explicit) {
      if (!keyLooksValid(explicit)) { c.keyError = `Card Key "${explicit}" doesn't parse (missing a __ separator?).`; continue; }
      c.key = explicit;
      c.explicitKey = true;
      // Serial # cell is display-only; a mismatch is a warning, the key wins.
      const serialCell = cellStr(c.idCells.serial);
      if (serialCell) {
        const { parallelId, serialNum } = parseKey(explicit);
        const ser = parseSerialCell(serialCell);
        const cellIdx = ser ? (ser.copy !== undefined ? ser.copy : ser.num) : null;
        const keyIdx = serialNum !== null ? Number(serialNum) : null;
        if (cellIdx !== null && keyIdx !== null && cellIdx !== keyIdx) {
          warn(c, `Serial # says "${serialCell}" but the Card Key ends __s__${serialNum}. The Card Key wins; Serial # is display-only.${parallelId ? '' : ''}`);
        }
      }
    } else {
      const res = resolveKey(c.idCells, setData, ctx && ctx.registry);
      if (res.error) { c.keyError = res.error; c.keyCandidates = res.candidates || []; continue; }
      c.key = res.key;
      c.explicitKey = false;
      // A keyless row whose constructed key carries a copy index (no parallel)
      // is a copy HINT: never an identity, never merged into an existing copy
      // (the brief's renumbering trap). It skips dedupe and exact-match below.
      const { parallelId, serialNum } = parseKey(res.key);
      c.isCopyHint = serialNum !== null && !parallelId;
    }
  }

  // 3. In-file duplicates: same resolved key twice -> last row wins.
  // Copy-hint rows are exempt — two "copy 1" rows are two copies, not a dupe.
  const lastForKey = new Map();
  for (const c of candidates) if (c.key && !c.isCopyHint) lastForKey.set(c.key, c);
  for (const c of candidates) {
    if (c.key && !c.isCopyHint && lastForKey.get(c.key) !== c) {
      c.duplicateOf = lastForKey.get(c.key).rowNum;
      warn(c, `Same card as row ${lastForKey.get(c.key).rowNum} (${c.key}) — the last row wins, this one is ignored.`);
    }
  }

  // 4. Classify.
  const existingByKey = new Map((existingRows || []).map((r) => [r.card_key, r]));
  // Copy indices already taken per base key (existing rows + this import).
  const takenIdx = new Map(); // baseKey -> Set of ints
  for (const r of existingRows || []) {
    const { parallelId, serialNum } = parseKey(r.card_key || '');
    if (serialNum !== null && !parallelId && /^\d+$/.test(serialNum)) {
      const b = baseOf(r.card_key);
      if (!takenIdx.has(b)) takenIdx.set(b, new Set());
      takenIdx.get(b).add(Number(serialNum));
    }
  }
  const claimIdx = (base, i) => {
    if (!takenIdx.has(base)) takenIdx.set(base, new Set());
    takenIdx.get(base).add(i);
  };
  const nextFreeIdx = (base) => {
    const used = takenIdx.get(base) || new Set();
    let i = 1;
    while (used.has(i)) i++;
    return i;
  };

  const rows = [];
  for (const c of candidates) {
    if (c.keyError) { rows.push(fail(c, c.keyError)); continue; }
    if (c.duplicateOf) continue; // superseded by a later row; already warned

    // Coerce every present cell once; a bad cell fails the whole row.
    const fields = {};
    let rowError = null;
    for (const col of c.present) {
      const { value, error } = coerceValue(col, c.values[col]);
      if (error) { rowError = `${headerFor(col)}: ${error}`; break; }
      fields[col] = value;
    }
    if (rowError) { rows.push(fail(c, rowError, 'value')); continue; }

    const existing = c.isCopyHint ? undefined : existingByKey.get(c.key);
    if (existing) {
      // Update path — exact key match keeps its copy index untouched.
      const changes = [];
      for (const col of c.present) {
        if (!sameValue(col, existing[col], fields[col])) {
          changes.push({ col, header: headerFor(col), from: existing[col] ?? null, to: fields[col] });
        }
      }
      // status can't be cleared: NOT NULL in the DB. Empty cell = leave alone.
      if (c.present.has('status') && fields.status === null) {
        delete fields.status;
        const i = changes.findIndex((ch) => ch.col === 'status');
        if (i !== -1) changes.splice(i, 1);
      }
      rows.push({
        sheet: c.sheet, rowNum: c.rowNum,
        kind: changes.length ? 'update' : 'unchanged',
        key: c.key, fields, changes, warnings: [],
      });
      continue;
    }

    // New row.
    let key = c.key;
    const { setId, parallelId, serialNum } = parseKey(key);
    if (!setData[setId]) {
      rows.push(fail(c, `Card Key "${key}" points at set "${setId}", which isn't in the CK catalog.`));
      continue;
    }
    if (serialNum !== null && !parallelId) {
      // Multi-entry copy. For keyless rows the file's index is a hint, not an
      // identity: collision with an owned/claimed index -> next free index.
      const base = baseOf(key);
      const idx = Number(serialNum);
      if (!c.explicitKey && (takenIdx.get(base) || new Set()).has(idx)) {
        const free = nextFreeIdx(base);
        key = `${base}__s__${free}`;
        warn(c, `You already own copy ${idx} of ${base} — imported as copy ${free} instead of overwriting yours.`);
      }
      claimIdx(base, Number(parseKey(key).serialNum));
      // Parent suppression's mirror: a bare-parent row alongside children is
      // the phantom-parent shape the export suppresses. Warn, don't delete.
      if (existingByKey.has(base)) {
        warn(c, `You also own ${base} without a copy number. CK will show the numbered copies; consider removing the un-numbered one.`);
      }
    }

    if (fields.status === undefined || fields.status === null) fields.status = 'collected';
    rows.push({
      sheet: c.sheet, rowNum: c.rowNum,
      kind: 'new', key, fields, changes: [], warnings: [],
    });
  }

  // 5. Payloads. PostgREST bulk upserts need uniform keys per request, and
  // updates must carry ONLY present columns (absent column ≠ blank value), so
  // inserts and updates ship separately.
  const inserts = rows.filter((r) => r.kind === 'new')
    .map((r) => ({ card_key: r.key, ...r.fields }));
  const updates = rows.filter((r) => r.kind === 'update')
    .map((r) => ({ card_key: r.key, ...r.fields }));

  const counts = {
    new: inserts.length,
    update: updates.length,
    unchanged: rows.filter((r) => r.kind === 'unchanged').length,
    error: errors.length,
    warning: warnings.length,
  };

  return { rows, counts, errors, warnings, notes, inserts, updates };
}

const HEADER_FOR_COL = new Map(Object.entries(WRITABLE_HEADERS).map(([h, col]) => [col, h]));
function headerFor(col) { return HEADER_FOR_COL.get(col) || col; }

// ── Chunking ─────────────────────────────────────────────────────────

// Split payload rows into upsert chunks. PostgREST requires every object in a
// bulk request to share the same keys, so group by exact key set first.
export function chunkForWrite(payloadRows, size = 500) {
  const groups = new Map();
  for (const row of payloadRows) {
    const sig = Object.keys(row).sort().join('|');
    if (!groups.has(sig)) groups.set(sig, []);
    groups.get(sig).push(row);
  }
  const chunks = [];
  for (const rows of groups.values()) {
    for (let i = 0; i < rows.length; i += size) chunks.push(rows.slice(i, i + size));
  }
  return chunks;
}

// ── Template ─────────────────────────────────────────────────────────

// Set Reference tab for the template: one row per subset across the whole
// catalog — Set ID / Set Name / Subset ID / Subset Title / Parallels — so
// users can look up valid values without leaving the spreadsheet. Header has
// no Card # (and no Card Key), so an uploaded template's reference tab is
// skipped by classifyImport like any non-data tab.
export const SET_REFERENCE_COLUMNS = ['Set ID', 'Set Name', 'Subset ID', 'Subset Title', 'Parallels (use these names)'];

export function buildSetReferenceAoa(registry, setDataById) {
  const aoa = [SET_REFERENCE_COLUMNS];
  for (const meta of registry) {
    const sd = setDataById[meta.id];
    if (!sd) continue;
    for (const sub of sd.sets || []) {
      const isSketch = String(sub.type || '') === 'sketch';
      aoa.push([
        meta.id,
        meta.name || meta.id,
        sub.id,
        sub.title || sub.id,
        isSketch ? '' : parallelsForSubset(sd, sub).map((p) => p.name).join(', '),
      ]);
    }
  }
  return aoa;
}

// Read Me tab: the merge rules, so they travel with the file even when it
// circulates detached from the app. Single column, never a data tab.
export function buildReadMeAoa() {
  return [
    ['HOW TO IMPORT — COLLECTIONKEEPER'],
    [''],
    ['1. Fill one row per card on the Import tab. The two example rows show the two ways:'],
    ['   · UPDATE a card you own: keep its Card Key (from your export) — that column is the anchor.'],
    ['   · ADD a card: leave Card Key blank and fill Set ID + Subset + Card # (+ Parallel).'],
    ['   Valid Set IDs, subsets and parallel names are on the Set Reference tab.'],
    [''],
    ['2. Column rules: names must match the headers exactly (any order, extra columns ignored).'],
    ['   A column you delete is left alone in CK; a column you keep but leave empty CLEARS that field.'],
    [''],
    ['3. Status must be one of: collected, wishlist, trade, for_sale.'],
    [''],
    ['4. Private Notes are yours alone. They travel with your export and import so a'],
    ['   backup is complete, and they are never shown to anyone else on CK.'],
    [''],
    ['5. Photos, marketplace and sold state never change on import.'],
    [''],
    ['6. Nothing is written until you review the preview and confirm. Delete the example rows before importing.'],
  ];
}

// The import template: the exact export header row plus two example rows —
// an update by Card Key and a new add by Set ID + Subset + Card #.
// exportColumns is injected (EXPORT_COLUMNS) so the two files can't drift.
export function buildTemplateAoa(exportColumns) {
  const row = (cells) => exportColumns.map((h) => cells[h] ?? '');
  return [
    exportColumns,
    row({
      'Set ID': '2025-chrome',
      'Set Name': '2025 Topps Chrome Star Wars',
      'Subset': 'BASE SET',
      'Card #': '1',
      'Card Name': '(example: update a card you own — keep its Card Key)',
      'Status': 'collected',
      'Condition': 'near_mint',
      'Card Key': '2025-chrome__base__1',
    }),
    row({
      'Set ID': '2025-chrome',
      'Subset': 'base',
      'Card #': '2',
      'Card Name': '(example: add a card — leave Card Key blank, fill Set ID + Subset + Card #)',
      'Parallel': '',
      'Status': 'wishlist',
    }),
  ];
}
